import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  GitBranch, 
  Activity, 
  FileText, 
  Settings,
  LogOut,
  Mail,
  MailPlus,
  FolderKanban
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_salespipeline-2/artifacts/xswr6vhc_rt.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/leads', label: 'Leads', icon: Users },
  { path: '/pipeline', label: 'Pipeline', icon: GitBranch },
  { path: '/activities', label: 'Aktivitäten', icon: Activity },
  { path: '/call-guide', label: 'Leitfaden', icon: FileText },
  { path: '/email-settings', label: 'E-Mail-Einstellungen', icon: MailPlus },
];

const adminItems = [
  { path: '/campaigns', label: 'Kampagnen', icon: FolderKanban },
  { path: '/email-templates', label: 'E-Mail-Vorlagen', icon: Mail },
  { path: '/users', label: 'Benutzer', icon: Settings },
];

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="main-layout">
      {/* Sidebar */}
      <aside className="sidebar w-64 flex flex-col" data-testid="sidebar">
        <div className="p-6 border-b border-gray-800">
          <img 
            src={LOGO_URL} 
            alt="Kaufmanns Consulting" 
            className="h-10 object-contain"
            data-testid="logo"
          />
        </div>
        
        <nav className="flex-1 p-4 space-y-1" data-testid="navigation">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}
          
          {isAdmin && (
            <>
              <div className="pt-4 pb-2">
                <span className="px-4 text-xs text-gray-500 uppercase tracking-wider">Admin</span>
              </div>
              {adminItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-gray-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 text-gray-300 hover:text-white hover:bg-white/10"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[#E63946] text-white text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start text-sm">
                  <span className="font-medium">{user?.name}</span>
                  <span className="text-xs text-gray-500">{user?.role === 'admin' ? 'Admin' : 'Call Agent'}</span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mein Konto</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" data-testid="profile-link">
                  <Settings className="mr-2 h-4 w-4" />
                  Profil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-[#F8F9FA]" data-testid="main-content">
        {children}
      </main>
    </div>
  );
};
